/**
 * WebGL2 后端实现（DESIGN.md §2.8 的回退路径）。
 *
 * **这是 src 下唯一允许出现 `WebGL2RenderingContext` 等 WebGL 类型的地方**，
 * 与 `../webgpu/backend.ts` 对称。test/gpu/isolation.test.ts 守这条线。
 *
 * 与 WebGPU 的两处根本差异，全部在本文件内抹平，调用方不需要知道：
 *
 * 1. **浮点渲染目标要靠扩展**。WebGPU 规范保证 rgba16float 可渲染；WebGL2 上则要
 *    `EXT_color_buffer_float`（全精度）或 `EXT_color_buffer_half_float`（半精度），
 *    两者都没有就只剩 8 位。降级链在 {@link detectFloatSupport} 里，
 *    并如实反映到 `capabilities`——**不假装支持**，上层据此决定是否走 CPU 路径。
 *
 * 2. **行序相反**。WebGL 的帧缓冲原点在左下、`readPixels` 从底行开始读；
 *    抽象层约定原点左上（与文档坐标一致，见 ../backend.ts）。因此 readback
 *    必须翻转行序，否则两个后端的同一份对拍测试会上下颠倒地失败。
 */
import {
  BYTES_PER_PIXEL,
  DestroyedResourceError,
  validateTextureDescriptor,
  type BackendCapabilities,
  type BatchDescriptor,
  type DrawDescriptor,
  type GpuBackend,
  type GpuProgram,
  type GpuTexture,
  type ProgramDescriptor,
  type ReadbackResult,
  type TextureDescriptor,
  type TextureFormat,
  type UniformValue,
} from '../backend';

/** 浮点渲染能力，由扩展探测得出。 */
export interface FloatSupport {
  /** 能渲染到 32 位浮点（EXT_color_buffer_float）。 */
  readonly full: boolean;
  /** 能渲染到 16 位半精度（上者或 EXT_color_buffer_half_float）。 */
  readonly half: boolean;
}

/**
 * 探测浮点渲染目标支持，形成 full → half → 8bit 的降级链。
 *
 * 抽出成纯函数是为了能用假的 gl 对象把三档都测到——真实设备上永远只走其中一条。
 */
export function detectFloatSupport(gl: {
  getExtension(name: string): unknown;
}): FloatSupport {
  const full = gl.getExtension('EXT_color_buffer_float') !== null;
  const half = full || gl.getExtension('EXT_color_buffer_half_float') !== null;
  return { full, half };
}

/**
 * 按能力把请求的格式降级到实际可用的格式。
 *
 * 返回的可能不是调用方要的格式——这是**有意暴露**的：调用方通过 readback 的
 * 结果类型与 `capabilities.renderToFloat16` 能看出发生了降级，
 * 而不是拿到一张悄悄被截断成 8 位的图还以为是 HDR。
 */
export function degradeFormat(format: TextureFormat, support: FloatSupport): TextureFormat {
  if (format === 'rgba16float') return support.half ? 'rgba16float' : 'rgba8unorm';
  if (format === 'r16float') return support.half ? 'r16float' : 'r8unorm';
  return format;
}

interface GlFormatTriple {
  readonly internalFormat: number;
  readonly format: number;
  readonly type: number;
}

function glFormatFor(gl: WebGL2RenderingContext, format: TextureFormat): GlFormatTriple {
  switch (format) {
    case 'rgba8unorm':
      return { internalFormat: gl.RGBA8, format: gl.RGBA, type: gl.UNSIGNED_BYTE };
    case 'rgba16float':
      return { internalFormat: gl.RGBA16F, format: gl.RGBA, type: gl.HALF_FLOAT };
    case 'r8unorm':
      return { internalFormat: gl.R8, format: gl.RED, type: gl.UNSIGNED_BYTE };
    case 'r16float':
      return { internalFormat: gl.R16F, format: gl.RED, type: gl.HALF_FLOAT };
  }
}

/** 半精度转 f32，与 WebGPU 后端共用同一套语义（含次正规数与 Inf/NaN）。 */
export function float16ToFloat32(bits: number): number {
  const sign = (bits & 0x8000) !== 0 ? -1 : 1;
  const exponent = (bits >> 10) & 0x1f;
  const mantissa = bits & 0x03ff;
  if (exponent === 0) return sign * mantissa * 2 ** -24;
  if (exponent === 0x1f) return mantissa === 0 ? sign * Infinity : Number.NaN;
  return sign * (1 + mantissa / 1024) * 2 ** (exponent - 15);
}

class Gl2Texture implements GpuTexture {
  #destroyed = false;

  constructor(
    readonly gl: WebGL2RenderingContext,
    readonly handle: WebGLTexture,
    readonly width: number,
    readonly height: number,
    /** 实际使用的格式，可能因能力不足而低于请求值。 */
    readonly format: TextureFormat,
    /** 调用方原本请求的格式，用于诊断降级。 */
    readonly requestedFormat: TextureFormat,
    readonly renderTarget: boolean,
  ) {}

  get destroyed(): boolean {
    return this.#destroyed;
  }

  native(): WebGLTexture {
    if (this.#destroyed) throw new DestroyedResourceError('纹理');
    return this.handle;
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.gl.deleteTexture(this.handle);
  }
}

class Gl2Program implements GpuProgram {
  #destroyed = false;

  constructor(
    readonly gl: WebGL2RenderingContext,
    readonly program: WebGLProgram,
  ) {}

  get destroyed(): boolean {
    return this.#destroyed;
  }

  native(): WebGLProgram {
    if (this.#destroyed) throw new DestroyedResourceError('着色器程序');
    return this.program;
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.gl.deleteProgram(this.program);
  }
}

function compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (shader === null) throw new Error('无法创建着色器对象。');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? '(无日志)';
    gl.deleteShader(shader);
    // 把源码一起报出来：GLSL 的报错只给行号，没有源码根本对不上。
    throw new Error(`着色器编译失败：${log}\n--- 源码 ---\n${source}`);
  }
  return shader;
}

class WebGl2Backend implements GpuBackend {
  readonly kind = 'webgl2' as const;
  readonly capabilities: BackendCapabilities;
  readonly floatSupport: FloatSupport;
  readonly #framebuffer: WebGLFramebuffer;
  #destroyed = false;

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.floatSupport = detectFloatSupport(gl);
    this.capabilities = {
      renderToFloat16: this.floatSupport.half,
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    };
    const fb = gl.createFramebuffer();
    if (fb === null) throw new Error('无法创建帧缓冲。');
    this.#framebuffer = fb;
  }

  #assertAlive(): void {
    if (this.#destroyed) throw new DestroyedResourceError('GPU 后端');
  }

  createTexture(descriptor: TextureDescriptor): GpuTexture {
    this.#assertAlive();
    validateTextureDescriptor(descriptor, this.capabilities.maxTextureSize);

    const gl = this.gl;
    const actual = degradeFormat(descriptor.format, this.floatSupport);
    // texStorage2D 只需要 internalFormat；format/type 留给将来的 texSubImage 上传路径
    const { internalFormat } = glFormatFor(gl, actual);

    const handle = gl.createTexture();
    if (handle === null) throw new Error('无法创建纹理。');

    gl.bindTexture(gl.TEXTURE_2D, handle);
    gl.texStorage2D(gl.TEXTURE_2D, 1, internalFormat, descriptor.width, descriptor.height);
    // 线性过滤对浮点纹理未必可用，且合成器按 tile 精确对齐采样，最近邻更安全。
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);

    return new Gl2Texture(
      gl,
      handle,
      descriptor.width,
      descriptor.height,
      actual,
      descriptor.format,
      descriptor.renderTarget === true,
    );
  }

  writeTexture(texture: GpuTexture, data: Uint8Array): void {
    this.#assertAlive();
    if (!(texture instanceof Gl2Texture)) {
      throw new TypeError('writeTexture 收到的不是本后端创建的纹理。');
    }
    if (texture.format !== 'rgba8unorm' && texture.format !== 'r8unorm') {
      throw new RangeError(`writeTexture 目前只支持 8 位格式，收到 ${texture.format}。`);
    }
    const bpp = BYTES_PER_PIXEL[texture.format];
    if (data.length !== texture.width * texture.height * bpp) {
      throw new RangeError(
        `writeTexture 数据长度 ${data.length} 与 ${texture.width}×${texture.height}(${texture.format}) 不符。`,
      );
    }
    const gl = this.gl;
    const triple = glFormatFor(gl, texture.format);
    // 抽象层约定原点左上，而 GL 纹理的 v=0 在下：readback 已经做了行翻转，
    // 上传必须做同样的预翻转，否则「上传-读回」不是恒等（往返测试会抓住）
    const rowBytes = texture.width * bpp;
    const flipped = new Uint8Array(data.length);
    for (let y = 0; y < texture.height; y++) {
      flipped.set(
        data.subarray(y * rowBytes, (y + 1) * rowBytes),
        (texture.height - 1 - y) * rowBytes,
      );
    }
    gl.bindTexture(gl.TEXTURE_2D, texture.native());
    // R8 每行字节数可能不是 4 的倍数，默认 4 字节对齐会把行错位
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      0,
      texture.width,
      texture.height,
      triple.format,
      triple.type,
      flipped,
    );
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  createProgram(descriptor: ProgramDescriptor): GpuProgram {
    this.#assertAlive();
    const gl = this.gl;
    const { vertex, fragment } = descriptor.source.glsl;

    const vs = compileShader(gl, gl.VERTEX_SHADER, vertex);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragment);
    const program = gl.createProgram();
    if (program === null) throw new Error('无法创建着色器程序。');

    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    // 链接后 shader 对象即可释放，程序会保留它们的引用。
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) ?? '(无日志)';
      gl.deleteProgram(program);
      throw new Error(`着色器链接失败：${log}`);
    }
    return new Gl2Program(gl, program);
  }

  draw(descriptor: DrawDescriptor): void {
    this.#assertAlive();
    const gl = this.gl;
    const { program, target, vertexCount, uniforms, clear } = descriptor;

    if (!(target instanceof Gl2Texture)) {
      throw new TypeError('渲染目标必须由同一个 WebGL2 后端创建。');
    }
    if (!(program instanceof Gl2Program)) {
      throw new TypeError('着色器程序必须由同一个 WebGL2 后端创建。');
    }
    if (!target.renderTarget) {
      throw new Error('该纹理创建时未声明 renderTarget: true，不能作为渲染目标。');
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.#framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      target.native(),
      0,
    );

    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      // 最常见的原因是浮点格式不可渲染而扩展探测没覆盖到的边角情况。
      throw new Error(`帧缓冲不完整（status=0x${status.toString(16)}），格式 ${target.format}。`);
    }

    gl.viewport(0, 0, target.width, target.height);

    if (clear !== undefined) {
      gl.clearColor(clear[0], clear[1], clear[2], clear[3]);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }

    if (vertexCount > 0) {
      gl.useProgram(program.native());
      this.#applyUniforms(program.native(), uniforms);
      gl.drawArrays(gl.TRIANGLES, 0, vertexCount);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  #applyUniforms(
    program: WebGLProgram,
    uniforms: Readonly<Record<string, UniformValue>> | undefined,
  ): void {
    if (uniforms === undefined) return;
    const gl = this.gl;
    let textureUnit = 0;

    for (const name of Object.keys(uniforms).sort()) {
      const location = gl.getUniformLocation(program, name);
      // 未被使用的 uniform 会被 GLSL 编译器优化掉，location 为 null 属正常情况。
      if (location === null) continue;
      const value = uniforms[name];

      if (typeof value === 'number') gl.uniform1f(location, value);
      else if (Array.isArray(value)) {
        const v = value as readonly number[];
        if (v.length === 2) gl.uniform2fv(location, v);
        else if (v.length === 3) gl.uniform3fv(location, v);
        else if (v.length === 4) gl.uniform4fv(location, v);
        else gl.uniform1fv(location, v);
      } else if (value instanceof Gl2Texture) {
        gl.activeTexture(gl.TEXTURE0 + textureUnit);
        gl.bindTexture(gl.TEXTURE_2D, value.native());
        gl.uniform1i(location, textureUnit);
        textureUnit++;
      }
    }
  }

  /**
   * 批量绘制。
   *
   * WebGL2 没有 command buffer 概念，省不掉"提交"这一步，但能省掉每次都
   * 重新绑定 framebuffer 与检查完整性——那两项在逐 tile 合成里同样是固定开销大头。
   * 程序切换也做了去重：连续画同一个 program 时不重复 useProgram。
   */
  drawBatch(descriptor: BatchDescriptor): void {
    this.#assertAlive();
    const gl = this.gl;
    const { target, items, clear } = descriptor;

    if (!(target instanceof Gl2Texture)) {
      throw new TypeError('渲染目标必须由同一个 WebGL2 后端创建。');
    }
    if (!target.renderTarget) {
      throw new Error('该纹理创建时未声明 renderTarget: true，不能作为渲染目标。');
    }
    // 不能因为 items 空就整个早退——那样 `clear` 也一起被跳掉了，target
    // 留着上一次渲染（纹理池复用回来的）的旧内容。一个 tile 的所有图层
    // 全部隐藏时 items 恰好是空数组，这条早退会让"隐藏后再显示"这类
    // 操作看起来卡在隐藏前那一帧——下面照常绑好帧缓冲执行 clear，
    // 循环体对空数组自然不做任何 draw。

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.#framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      target.native(),
      0,
    );
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      throw new Error(`帧缓冲不完整（status=0x${status.toString(16)}），格式 ${target.format}。`);
    }

    gl.viewport(0, 0, target.width, target.height);
    if (clear !== undefined) {
      gl.clearColor(clear[0], clear[1], clear[2], clear[3]);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }

    let boundProgram: WebGLProgram | null = null;
    for (const item of items) {
      if (!(item.program instanceof Gl2Program)) {
        throw new TypeError('着色器程序必须由同一个 WebGL2 后端创建。');
      }
      if (item.vertexCount <= 0) continue;

      const native = item.program.native();
      if (native !== boundProgram) {
        gl.useProgram(native);
        boundProgram = native;
      }

      if (item.viewport !== undefined) {
        const [x, y, w, h] = item.viewport;
        // 抽象层约定原点左上，WebGL 视口原点在左下，这里翻回去
        gl.viewport(x, target.height - y - h, w, h);
      }

      this.#applyUniforms(native, item.uniforms);
      gl.drawArrays(gl.TRIANGLES, 0, item.vertexCount);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  async readback(texture: GpuTexture, signal?: AbortSignal): Promise<ReadbackResult> {
    this.#assertAlive();
    const aborted = (): boolean => signal?.aborted === true;
    if (aborted()) throw new Error('readback 已取消。');
    if (!(texture instanceof Gl2Texture)) {
      throw new TypeError('纹理必须由同一个 WebGL2 后端创建。');
    }

    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.#framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      texture.native(),
      0,
    );

    try {
      const { width, height, format } = texture;
      const channels = format.startsWith('rgba') ? 4 : 1;
      const glFormat = channels === 4 ? gl.RGBA : gl.RED;
      const isFloat = format.endsWith('float');

      // 单通道时每行字节数未必是 4 的倍数，默认 PACK_ALIGNMENT=4 会按补齐后的
      // 行宽打包，缓冲区解读整体错位（r8 宽 5 的往返测试抓出来的）
      gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
      let rows: Uint8Array | Uint16Array;
      if (isFloat) {
        const buf = new Uint16Array(width * height * channels);
        gl.readPixels(0, 0, width, height, glFormat, gl.HALF_FLOAT, buf);
        rows = buf;
      } else {
        const buf = new Uint8Array(width * height * channels);
        gl.readPixels(0, 0, width, height, glFormat, gl.UNSIGNED_BYTE, buf);
        rows = buf;
      }
      gl.pixelStorei(gl.PACK_ALIGNMENT, 4);

      if (aborted()) throw new Error('readback 已取消。');

      const flipped = flipRows(rows, width * channels, height);
      if (!isFloat) return flipped as Uint8Array;

      const halfBits = flipped as Uint16Array;
      const out = new Float32Array(halfBits.length);
      for (let i = 0; i < halfBits.length; i++) out[i] = float16ToFloat32(halfBits[i]!);
      return out;
    } finally {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.gl.deleteFramebuffer(this.#framebuffer);
    // 主动释放上下文，否则浏览器的 WebGL 上下文数量上限（通常 16）很快被耗尽。
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}

/**
 * 翻转行序：WebGL 的 `readPixels` 从帧缓冲底行开始读，而抽象层约定原点在左上。
 *
 * @param elementsPerRow 每行的**元素**数（不是字节数），已含通道数。
 */
export function flipRows<T extends Uint8Array | Uint16Array>(
  data: T,
  elementsPerRow: number,
  height: number,
): T {
  const out = new (data.constructor as new (n: number) => T)(data.length);
  for (let y = 0; y < height; y++) {
    const src = (height - 1 - y) * elementsPerRow;
    (out as unknown as { set(a: ArrayLike<number>, o: number): void }).set(
      data.subarray(src, src + elementsPerRow),
      y * elementsPerRow,
    );
  }
  return out;
}

/**
 * 创建 WebGL2 后端。与 WebGPU 版对称：**不可用时返回 `null`，不抛异常**。
 *
 * @param canvas 省略时自建一个离屏 canvas。传入是为了让调用方能控制生命周期
 *   （浏览器对同时存在的 WebGL 上下文数量有硬上限）。
 */
export function createWebGL2Backend(
  canvas?: HTMLCanvasElement | OffscreenCanvas,
): GpuBackend | null {
  try {
    const surface =
      canvas ??
      (typeof OffscreenCanvas === 'function'
        ? new OffscreenCanvas(1, 1)
        : globalThis.document?.createElement('canvas'));
    if (surface === undefined || surface === null) return null;

    const gl = surface.getContext('webgl2', {
      alpha: true,
      antialias: false,
      // 合成结果由我们自己管理 alpha，交给浏览器预乘会二次变换。
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
    });
    if (gl === null) return null;

    return new WebGl2Backend(gl as WebGL2RenderingContext);
  } catch (error) {
    console.warn('[gpu] WebGL2 初始化失败。', error);
    return null;
  }
}
